import { Typography } from '@breetometer/components';
import { CopyIcon } from 'BreetAssets';
import { RowSection } from 'BreetComponents';
import {
	copyToClipboard,
	type CURRENCYCODETYPE,
	CURRENCYCONFIG,
	formatCurrencyAmount,
	formatDateTime,
	getFxConversionType,
	toFixedPrecision,
} from 'BreetHelpers';
import { useCurrencyOutput, useCurrentFxRate } from 'BreetHooks';
import { useMemo } from 'react';

import type { IConversionsProps } from '../DialogWithdrawalHistoryDetails.helpers';
import RowBalance from './RowBalance';
import RowStatusNote from './RowStatusNote';

const Conversions = ({ withdrawalHistoryData }: IConversionsProps) => {
	const currencyOutput = useCurrencyOutput();

	const conversionType = getFxConversionType(
		withdrawalHistoryData.doc.from?.toUpperCase() as CURRENCYCODETYPE,
		withdrawalHistoryData.doc.to?.toUpperCase() as CURRENCYCODETYPE
	);

	const currencyFxRate = useCurrentFxRate({ conversionType });

	const userFiatConfig =
		CURRENCYCONFIG[withdrawalHistoryData.doc.to ? (withdrawalHistoryData.doc.to.toUpperCase() as CURRENCYCODETYPE) : 'USD'];

	const defaultAmount = formatCurrencyAmount(
		Number(
			toFixedPrecision(
				userFiatConfig.code === 'GHS'
					? withdrawalHistoryData.doc.amount / withdrawalHistoryData.doc.rate
					: withdrawalHistoryData.doc.rate * withdrawalHistoryData.doc.amount,
				2
			)
		),
		userFiatConfig.code
	);

	const fiatAmount =
		withdrawalHistoryData.doc.rate > 0
			? formatCurrencyAmount(
					Number((withdrawalHistoryData.doc.amount / withdrawalHistoryData.doc.rate).toFixed(3)),
					userFiatConfig.code,
					3
				)
			: 'Error: Invalid rate';

	const amount = withdrawalHistoryData.doc.isBuyCrypto ? fiatAmount : defaultAmount;

	const isSwapFiat = currencyFxRate.symbol.from !== '$' && currencyFxRate.symbol.to !== '$';

	const beforeAfterObj: Record<'swap' | 'buy-crypto' | 'default', { before: number; after: number }> = {
		'swap': {
			before: withdrawalHistoryData.doc.swapBalances?.[currencyFxRate.symbol.from === '₦' ? 'ngnBefore' : 'ghsBefore'] ?? 0,
			after: withdrawalHistoryData.doc.swapBalances?.[currencyFxRate.symbol.from === '₦' ? 'ngnAfter' : 'ghsAfter'] ?? 0,
		},
		'buy-crypto': {
			before: withdrawalHistoryData.doc.fiatBefore,
			after: withdrawalHistoryData.doc.fiatAfter,
		},
		'default': {
			before: withdrawalHistoryData.doc.usdBefore,
			after: withdrawalHistoryData.doc.usdAfter,
		},
	};

	const beforeAfterType = useMemo((): 'swap' | 'buy-crypto' | 'default' => {
		if (isSwapFiat) return 'swap';
		if (withdrawalHistoryData.doc.isBuyCrypto) return 'buy-crypto';
		return 'default';
	}, [isSwapFiat, withdrawalHistoryData.doc.isBuyCrypto]);

	const beforeAfterValues = beforeAfterObj[beforeAfterType];

	return (
		<>
			<div className='transactiondetails_header'>
				<Typography as='p'>Conversions</Typography>
				<Typography as='h5'>
					{currencyFxRate.symbol.from}
					{currencyOutput(withdrawalHistoryData.amount)}
				</Typography>
			</div>

			<div className='transactiondetails_itemscontainer bg-primary-500'>
				<RowSection.Tuple
					leftLabel={`${userFiatConfig.code} Amount`}
					leftComponent={
						<div className='transactiondetails_value'>
							<Typography className='color-neutral-50'>{amount}</Typography>
						</div>
					}
					rightLabel='Rate'
					rightComponent={
						<div className='transactiondetails_value'>
							<Typography className='color-neutral-50'>
								{currencyFxRate.rateInfoSymbol.from}
								{currencyOutput(withdrawalHistoryData.doc.rate)} / {currencyFxRate.rateInfoSymbol.to}
							</Typography>
						</div>
					}
				/>

				<RowSection.Single
					label='Description'
					component={
						<div className='transactiondetails_value'>
							<Typography className='color-neutral-50'>{withdrawalHistoryData.description}</Typography>
						</div>
					}
				/>

				<RowSection.Single
					label='Transaction ID'
					component={
						<div className='transactiondetails_value'>
							<Typography className='color-neutral-50'>{withdrawalHistoryData.doc._id}</Typography>
							<CopyIcon
								variant='colored'
								focusable
								onClick={copyToClipboard(withdrawalHistoryData.doc._id, 'Reference ID copied successfully')}
								className='cursor_pointer hide-during-screenshot'
							/>
						</div>
					}
				/>

				<RowStatusNote status={withdrawalHistoryData.status} />

				<RowBalance
					symbol={currencyFxRate.symbol.from}
					before={currencyOutput(beforeAfterValues.before)}
					after={currencyOutput(beforeAfterValues.after)}
				/>

				<RowSection.Single
					label='Date'
					component={
						<div className='transactiondetails_value'>
							<Typography className='color-neutral-50'>{formatDateTime(withdrawalHistoryData.updatedAt)}</Typography>
						</div>
					}
				/>
			</div>
		</>
	);
};

export default Conversions;
